import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { GoPlus } from "react-icons/go";
import { IoIosInformationCircleOutline } from "react-icons/io";
import { TbEdit, TbTrash } from "react-icons/tb";
import { formatAmountWithCurrency } from '../../utils/stringUtils';
import Popup from '../Elements/Popup';
import Dropdown from '../Elements/Dropdown';
import useIsMobile from '../../hooks/useIsMobile';
import { useToast } from '../../context/ToastContext';

export default function ReusableTable({ 
  title, 
  data, 
  onDataChange, 
  addRowLabel = "Add item",
  currencySymbol = "$",
  currency = "USD",
  showTotal = true,
  totalLabel = "Total",
  customTotalAmount = null,
  tooltipContent = null
}) {
  // Helper function to ensure we always have at least one row
  // Now relies on parent components to provide properly initialized data
  const initializeData = useCallback((inputData) => {
    if (!inputData || inputData.length === 0) {
      return [];
    }
    return inputData;
  }, []);

  const isMobile = useIsMobile();
  const { showToast } = useToast();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [addRowPopup, setAddRowPopup] = useState({ isOpen: false, category: '', amount: '' });
  const [editPopup, setEditPopup] = useState({ isOpen: false, rowId: null, category: '', amount: '' });
  const [dropdownOpen, setDropdownOpen] = useState({});
  const [localData, setLocalData] = useState(() => initializeData(data));
  const [inputValues, setInputValues] = useState(() => {
    // Initialize input values from data
    const initialInputValues = {};
    const initialData = initializeData(data);
    initialData.forEach(item => {
      if (item.id) {
        initialInputValues[item.id] = {
          category: item.category || '',
          amount: item.amount === 0 ? '' : item.amount.toString()
        };
      }
    });
    return initialInputValues;
  });
  const debounceTimeoutRef = useRef(null);
  const isUserOperatingRef = useRef(false); // Track if user is actively operating
  const previousDataRef = useRef(data); // Track previous data to detect meaningful changes
  const lastSaveDataRef = useRef(data); // Track the last data we saved

  // Helper function to compare data arrays
  const areDataArraysEqual = useCallback((arr1, arr2) => {
    if (!arr1 || !arr2 || arr1.length !== arr2.length) return false;
    return arr1.every((item1, index) => {
      const item2 = arr2[index];
      return item1.id === item2.id && 
             item1.category === item2.category && 
             item1.amount === item2.amount;
    });
  }, []);

  // Update local data when props change (but not during user input)
  useEffect(() => {
    // Only update from props if:
    // 1. User is not actively operating, AND
    // 2. The data has actually changed from what we last saved, AND
    // 3. The data is different from our current local data
    const dataChanged = !areDataArraysEqual(data, previousDataRef.current);
    const isDifferentFromLocal = !areDataArraysEqual(data, localData);
    const isDifferentFromLastSave = !areDataArraysEqual(data, lastSaveDataRef.current);
    
    if (!isUserOperatingRef.current && dataChanged && (isDifferentFromLocal || isDifferentFromLastSave)) {
      // Ensure we always have at least one row
      const dataToSet = initializeData(data);
      setLocalData(dataToSet);
      // Initialize input values for existing data
      const newInputValues = {};
      dataToSet.forEach(item => {
        if (item.id) {
          newInputValues[item.id] = {
            category: item.category || '',
            amount: item.amount === 0 ? '' : item.amount.toString()
          };
        }
      });
      setInputValues(newInputValues);
    }
    
    // Update the previous data reference
    previousDataRef.current = data;
  }, [data, localData, areDataArraysEqual, initializeData]);

  // Ensure each row has a unique ID
  // Use a ref to track the last assigned ID to ensure stability
  const nextIdRef = useRef(0);
  const ensureRowIds = useCallback((rows) => {
    return rows.map((row, index) => {
      if (row.id) {
        return row;
      }
      // If row doesn't have an ID, create a stable one
      const newId = `generated-row-${nextIdRef.current++}`;
      return { ...row, id: newId };
    });
  }, []);

  // Debounced save function
  const debouncedSave = useCallback((newData) => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    
    debounceTimeoutRef.current = setTimeout(() => {
      onDataChange(newData);
      // Update the last save data reference
      lastSaveDataRef.current = newData;
      // Reset user operating flag after save completes
      isUserOperatingRef.current = false;
    }, 500); // 500ms delay
  }, [onDataChange]);

  const addRow = () => {
    // On mobile, show popup instead
    if (isMobile) {
      setAddRowPopup({ isOpen: true, category: '', amount: '' });
      return;
    }

    // Desktop: Add row directly
    // Set flag to prevent props from overriding local state
    isUserOperatingRef.current = true;

    const newRow = { category: '', amount: 0, id: `row-${Date.now()}-${localData.length}` };
    const newData = [...localData, newRow];
    setLocalData(newData);

    // Initialize input values for new row
    setInputValues(prev => ({
      ...prev,
      [newRow.id]: {
        category: '',
        amount: ''
      }
    }));

    debouncedSave(newData);
    showToast('Item added successfully', 'success');
  };

  const handleAddRowFromPopup = () => {
    // Set flag to prevent props from overriding local state
    isUserOperatingRef.current = true;

    const newRow = {
      category: addRowPopup.category,
      amount: parseFloat(addRowPopup.amount) || 0,
      id: `row-${Date.now()}-${localData.length}`
    };
    const newData = [...localData, newRow];
    setLocalData(newData);

    // Initialize input values for new row
    setInputValues(prev => ({
      ...prev,
      [newRow.id]: {
        category: addRowPopup.category,
        amount: addRowPopup.amount
      }
    }));

    debouncedSave(newData);
    showToast('Item added successfully', 'success');

    // Close popup and reset form
    setAddRowPopup({ isOpen: false, category: '', amount: '' });
  };

  const handleOpenEdit = (rowId) => {
    const row = localData.find(item => item.id === rowId);
    setEditPopup({
      isOpen: true,
      rowId: rowId,
      category: row?.category || '',
      amount: row?.amount?.toString() || ''
    });
  };

  const handleSaveEdit = () => {
    if (!editPopup.rowId) return;

    // Set flag to prevent props from overriding local state
    isUserOperatingRef.current = true;

    // Update input values
    setInputValues(prev => ({
      ...prev,
      [editPopup.rowId]: {
        category: editPopup.category,
        amount: editPopup.amount
      }
    }));

    // Update local data
    const newData = localData.map(item => {
      if (item.id === editPopup.rowId) {
        return {
          ...item,
          category: editPopup.category,
          amount: parseFloat(editPopup.amount) || 0
        };
      }
      return item;
    });

    setLocalData(newData);
    debouncedSave(newData);
    showToast('Item updated successfully', 'success');

    // Close popup and reset
    setEditPopup({ isOpen: false, rowId: null, category: '', amount: '' });
  };

  const updateRow = (rowId, field, value) => {
    // Set flag to prevent props from overriding local state
    isUserOperatingRef.current = true;
    
    // Update input values immediately for responsive UI
    setInputValues(prev => ({
      ...prev,
      [rowId]: {
        ...prev[rowId],
        [field]: value
      }
    }));

    // Update local data with processed values
    const newData = localData.map(item => {
      if (item.id === rowId) {
        if (field === 'amount') {
          // Allow only numbers and decimal point
          const numericValue = value.replace(/[^0-9.]/g, '');
          // Prevent multiple decimal points
          const parts = numericValue.split('.');
          const cleanValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : numericValue;
          // Store as number, but handle empty string case properly
          const numValue = cleanValue === '' ? 0 : parseFloat(cleanValue) || 0;
          return { ...item, [field]: numValue };
        } else {
          return { ...item, [field]: value };
        }
      }
      return item;
    });
    
    setLocalData(newData);
    debouncedSave(newData);
  };

  const removeRow = (rowId) => {
    if (localData.length > 1) {
      // Set flag to prevent props from overriding local state
      isUserOperatingRef.current = true;

      const newData = localData.filter(item => item.id !== rowId);
      setLocalData(newData);

      // Remove input values for deleted row
      setInputValues(prev => {
        const newInputValues = { ...prev };
        delete newInputValues[rowId];
        return newInputValues;
      });

      debouncedSave(newData);
      showToast('Item deleted successfully', 'success');
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const totalAmount = customTotalAmount !== null ? customTotalAmount : localData.reduce((sum, item) => sum + Number(item.amount || 0), 0);

  // Ensure all rows have IDs - memoized to prevent unnecessary array recreation
  const dataWithIds = useMemo(() => ensureRowIds(localData), [localData, ensureRowIds]);

  return (
    <div className="w-full h-full card-base-border card-base card-base-background flex flex-col">
      {/* Header */}
      <div className="p-3 card-base-border-bottom flex-shrink-0">
        <div className="flex items-center gap-2">
          <h3 className="text-xl font-semibold">{title}</h3>
          {tooltipContent && (
            <button
              onClick={() => setIsPopupOpen(true)}
              className="cursor-pointer"
            >
              <IoIosInformationCircleOutline className="transition-colors" size={16} />
            </button>
          )}
        </div>
      </div>
      
      {/* Table */}
      <div className="overflow-hidden flex-1 flex flex-col">
        {/* Total Row */}
        {showTotal && (
          <div className={`py-1.5 px-3 card-base-border-bottom ${
            totalAmount === 0 ? 'background-page-color' :
            totalLabel === "Overspent" ? 'bg-red-100' :
            totalLabel === "Left to assign" ? 'bg-accent-light' :
            'bg-green-100'
          }`}>
            <div className="grid grid-cols-2 gap-4 ">
              <div className="font-bold">{totalLabel}</div>
              <div className={`font-semibold text-right ${
                totalAmount === 0 ? '' :
                totalLabel === "Overspent" ? 'text-red-600' :
                totalLabel === "Left to assign" ? 'text-orange-600' :
                'text-green-600'
              }`}>
                {totalLabel === "Overspent" ? 
                  `-${formatAmountWithCurrency(totalAmount, currency)}` : 
                  formatAmountWithCurrency(totalAmount, currency)
                }
              </div>
            </div>
          </div>
        )}
        
        {/* Data Rows */}
        <div className="flex-1 overflow-y-auto">
          {dataWithIds.map((item) => (
            <div key={item.id} className="p-3 card-base-border-bottom">
              <div className="flex justify-between items-center gap-4">
                <div className="min-w-0">
                  <input
                    type="text"
                    value={inputValues[item.id]?.category || ''}
                    onChange={(e) => updateRow(item.id, 'category', e.target.value)}
                    placeholder="Category"
                    className="w-full bg-transparent border-none outline-none text-base md:text-sm"
                  />
                </div>
                <div className="flex items-center gap-3">
                  {/* Desktop: Amount input */}
                  <input
                    type="text"
                    value={inputValues[item.id]?.amount || ''}
                    onChange={(e) => updateRow(item.id, 'amount', e.target.value)}
                    className="hidden md:block w-24 card-base-border rounded px-2 py-1 text-right text-sm focus:outline-none focus:border-blue-500"
                    placeholder="0"
                  />
                  {/* Mobile: Amount plain text */}
                  <div className="md:hidden text-base text-right min-w-[6rem]">
                    {item.amount || 0}
                  </div>
                  {/* Desktop: X delete button */}
                  <button
                    onClick={() => removeRow(item.id)}
                    className={`hidden md:block cursor-pointer text-accent-light text-sm transition-opacity duration-200 ${
                      localData.length > 1 ? 'opacity-100' : 'opacity-0 pointer-events-none'
                    }`}
                  >
                    ×
                  </button>
                  {/* Mobile: ... menu dropdown */}
                  <div className="md:hidden" onClick={(e) => e.stopPropagation()}>
                    <Dropdown
                      options={[
                        {
                          text: "Edit",
                          onClick: () => handleOpenEdit(item.id),
                          icon: <TbEdit />,
                        },
                        ...(localData.length > 1 ? [{
                          text: "Delete",
                          onClick: () => removeRow(item.id),
                          icon: <TbTrash />,
                        }] : []),
                      ]}
                      iconComponent="circle"
                      isOpen={dropdownOpen[item.id] || false}
                      onOpenChange={(isOpen) => setDropdownOpen(prev => ({ ...prev, [item.id]: isOpen }))}
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {/* Add Row Button */}
        <div className="py-2 flex justify-center flex-shrink-0">
          <button
            onClick={addRow}
            className="w-10 h-10 md:w-8 md:h-8 p-2 flex items-center justify-center transition ease-in-out duration-300 icon-base z-10 cursor-pointer hover:drop-shadow-md hover:scale-110"
            title={addRowLabel}
          >
            <GoPlus className="w-5 h-5 md:w-4 md:h-4" />
          </button>
        </div>
      </div>

      {/* Tooltip Popup */}
      {tooltipContent && (
        <Popup
          isOpen={isPopupOpen}
          onClose={() => setIsPopupOpen(false)}
          title={title}
          buttonPrimaryCTA="Close"
          onClickPrimaryLink={() => setIsPopupOpen(false)}
        >
          <div className="text-sm">
            {tooltipContent}
          </div>
        </Popup>
      )}

      {/* Add Row Popup (Mobile) */}
      <Popup
        isOpen={addRowPopup.isOpen}
        onClose={() => setAddRowPopup({ isOpen: false, category: '', amount: '' })}
        title={addRowLabel}
        buttonPrimaryCTA="Add"
        buttonSecondaryCTA="Cancel"
        onClickPrimaryLink={handleAddRowFromPopup}
        onClickSecondaryLink={() => setAddRowPopup({ isOpen: false, category: '', amount: '' })}
      >
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium">Category</label>
            <input
              type="text"
              value={addRowPopup.category}
              onChange={(e) => setAddRowPopup(prev => ({ ...prev, category: e.target.value }))}
              placeholder="Enter category"
              className="card-base-border rounded px-3 py-2 text-base focus:outline-none focus:border-blue-500"
            />
          </div>
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium">Amount ({currency})</label>
            <input
              type="number"
              value={addRowPopup.amount}
              onChange={(e) => setAddRowPopup(prev => ({ ...prev, amount: e.target.value }))}
              placeholder="0"
              className="card-base-border rounded px-3 py-2 text-base focus:outline-none focus:border-blue-500"
            />
          </div>
        </div>
      </Popup>

      {/* Edit Popup (Mobile) */}
      <Popup
        isOpen={editPopup.isOpen}
        onClose={() => setEditPopup({ isOpen: false, rowId: null, category: '', amount: '' })}
        title="Edit item"
        buttonPrimaryCTA="Save"
        buttonSecondaryCTA="Cancel"
        onClickPrimaryLink={handleSaveEdit}
        onClickSecondaryLink={() => setEditPopup({ isOpen: false, rowId: null, category: '', amount: '' })}
      >
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium">Category</label>
            <input
              type="text"
              value={editPopup.category}
              onChange={(e) => setEditPopup(prev => ({ ...prev, category: e.target.value }))}
              placeholder="Enter category"
              className="card-base-border rounded px-3 py-2 text-base focus:outline-none focus:border-blue-500"
            />
          </div>
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium">Amount ({currency})</label>
            <input
              type="number"
              value={editPopup.amount}
              onChange={(e) => setEditPopup(prev => ({ ...prev, amount: e.target.value }))}
              placeholder="0"
              className="card-base-border rounded px-3 py-2 text-base focus:outline-none focus:border-blue-500"
            />
          </div>
        </div>
      </Popup>
    </div>
  );
}
